import type {
  ApiQueryParams,
  MarkAllNotificationsReadResult,
  NotificationKindFilter,
  UnreadNotificationsCount,
  UnreadNotificationsFilter,
  UserNotification,
  UserNotificationPage
} from '../contracts'
import { isApiHttpError, requestJson } from '../httpClient'
import { isRecord } from '../../utils/apiValidation'

export const NOTIFICATIONS_API_PREFIX = '/api/notifications'
export const NOTIFICATIONS_PAGE_SIZE = 20

const INVALID_PAGE_MESSAGE = 'Некорректный формат ответа API: ожидалась страница уведомлений.'
const INVALID_COUNT_MESSAGE = 'Некорректный формат ответа API: ожидался счётчик непрочитанных уведомлений.'
const INVALID_MARKED_COUNT_MESSAGE = 'Некорректный формат ответа API: ожидалось число отмеченных уведомлений.'

type NotificationsPageQueryContract = NonNullable<ApiQueryParams<'getUserNotifications'>>

export interface NotificationsPageParams {
  page: number
  unread: UnreadNotificationsFilter
  kind: NotificationKindFilter
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

/** Minimal guard: the feed draws every row by id, title and time and pages by the totals. */
function assertNotificationsPage(payload: unknown): UserNotificationPage {
  if (
    !isRecord(payload)
    || !Array.isArray(payload.items)
    || !payload.items.every((item) => (
      isRecord(item)
      && Number.isInteger(item.id)
      && typeof item.eventType === 'string'
      && typeof item.title === 'string'
      && typeof item.createdAtUtc === 'string'
    ))
    || !isNonNegativeInteger(payload.totalCount)
    || !isNonNegativeInteger(payload.totalPages)
  ) {
    throw new Error(INVALID_PAGE_MESSAGE)
  }
  return payload as unknown as UserNotificationPage
}

/** Page of the current user's notifications, newest first. */
export async function getNotificationsPage(
  apiBaseUrl: string,
  params: NotificationsPageParams,
  signal?: AbortSignal
): Promise<UserNotificationPage> {
  const query = {
    page: params.page,
    pageSize: NOTIFICATIONS_PAGE_SIZE,
    unread: params.unread,
    kind: params.kind
  } satisfies NotificationsPageQueryContract
  const search = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
    unread: query.unread,
    kind: query.kind
  })

  const payload = await requestJson<unknown>(`${apiBaseUrl}${NOTIFICATIONS_API_PREFIX}?${search.toString()}`, {
    method: 'GET',
    signal,
    fallbackErrorMessage: 'Не удалось загрузить уведомления.'
  })
  return assertNotificationsPage(payload)
}

export async function getUnreadNotificationsCount(apiBaseUrl: string, signal?: AbortSignal): Promise<number> {
  const payload = await requestJson<unknown>(
    `${apiBaseUrl}${NOTIFICATIONS_API_PREFIX}/unread-count`,
    {
      signal,
      fallbackErrorMessage: 'Не удалось загрузить число непрочитанных уведомлений.'
    }
  )
  if (!isRecord(payload) || !isNonNegativeInteger(payload.count)) {
    throw new Error(INVALID_COUNT_MESSAGE)
  }
  return (payload as UnreadNotificationsCount).count
}

/** Marks one notification read; `null` when it does not exist (for example, its transaction was rolled back). */
export async function markNotificationRead(apiBaseUrl: string, notificationId: number): Promise<UserNotification | null> {
  try {
    return await requestJson<UserNotification>(`${apiBaseUrl}${NOTIFICATIONS_API_PREFIX}/${notificationId}/read`, {
      method: 'POST',
      skipHandledStatuses: [404],
      fallbackErrorMessage: 'Не удалось отметить уведомление прочитанным.'
    })
  } catch (error) {
    if (isApiHttpError(error) && error.status === 404) {
      return null
    }
    throw error
  }
}

/** Marks every unread notification of the current user read; returns how many were marked. */
export async function markAllNotificationsRead(apiBaseUrl: string): Promise<number> {
  const payload = await requestJson<unknown>(
    `${apiBaseUrl}${NOTIFICATIONS_API_PREFIX}/read-all`,
    {
      method: 'POST',
      fallbackErrorMessage: 'Не удалось отметить уведомления прочитанными.'
    }
  )
  if (!isRecord(payload) || !isNonNegativeInteger(payload.markedCount)) {
    throw new Error(INVALID_MARKED_COUNT_MESSAGE)
  }
  return (payload as MarkAllNotificationsReadResult).markedCount
}
